/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { CalendarRange } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';

/**
 * Internal dependencies
 */
import type { StatsRange } from '@Types/messagingStats';

const WINDOW_OPTIONS = [
  { days: 1, label: 'Today' },
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 365, label: 'Last 365 days' },
] as const;

/** Longest custom range the stats API accepts, matching the longest relative window. */
const MAX_RANGE_DAYS = 365;
/** Select value shown while a custom range is active. */
const CUSTOM_VALUE = 'custom';

const today = () => DateTime.now().toISODate() as string;

/** `Jun 1 – Jun 8, 2026`, or `Jun 1, 2026` for a single day. */
const rangeLabel = ({ from, to }: { from: string; to: string }) => {
  const start = DateTime.fromISO(from);
  const end = DateTime.fromISO(to);
  if (from === to) return end.toFormat('LLL d, yyyy');
  return `${start.toFormat(start.year === end.year ? 'LLL d' : 'LLL d, yyyy')} – ${end.toFormat('LLL d, yyyy')}`;
};

/** The calendar days a relative window covers, to prefill the custom inputs. */
const datesOf = (range: StatsRange) => {
  if (!('days' in range)) return range;
  const end = DateTime.now();
  return {
    from: end.minus({ days: range.days - 1 }).toISODate() as string,
    to: end.toISODate() as string,
  };
};

export default function RangePicker({
  range,
  onChange,
}: {
  range: StatsRange;
  onChange: (range: StatsRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(datesOf(range).from);
  const [to, setTo] = useState(datesOf(range).to);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      const dates = datesOf(range);
      setFrom(dates.from);
      setTo(dates.to);
    }
    setOpen(next);
  };

  const handleApply = () => {
    if (!from || !to) {
      toastError('Pick a start and end date.');
      return;
    }
    if (to < from) {
      toastError('End date must be on or after start date.');
      return;
    }
    const span = DateTime.fromISO(to).diff(DateTime.fromISO(from), 'days').days + 1;
    if (span > MAX_RANGE_DAYS) {
      toastError(`Pick a range of at most ${MAX_RANGE_DAYS} days.`);
      return;
    }
    onChange({ from, to });
    setOpen(false);
  };

  const isCustom = !('days' in range);

  return (
    <div className="flex items-center gap-2">
      <Select
        value={isCustom ? CUSTOM_VALUE : String(range.days)}
        onValueChange={(value) => {
          if (value !== CUSTOM_VALUE) onChange({ days: Number(value) });
        }}
      >
        <SelectTrigger className="w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {WINDOW_OPTIONS.map((option) => (
            <SelectItem key={option.days} value={String(option.days)}>
              {option.label}
            </SelectItem>
          ))}
          {isCustom && (
            <SelectItem value={CUSTOM_VALUE}>{rangeLabel(range)}</SelectItem>
          )}
        </SelectContent>
      </Select>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <CalendarRange className="size-4" />
            Custom range
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="flex flex-col space-y-3">
            <p className="text-xs text-zinc-500">
              Whole days in the venue's time zone, both ends included.
            </p>
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="engagement-range-start">Start Date</Label>
              <Input
                id="engagement-range-start"
                type="date"
                value={from}
                max={to || today()}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="engagement-range-end">End Date</Label>
              <Input
                id="engagement-range-end"
                type="date"
                value={to}
                min={from || undefined}
                max={today()}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
